import type { WeeklyReportEmailTemplateProps } from "@/components/WeeklyReportEmailTemplate";
import WeeklyReportEmailTemplate from "@/components/WeeklyReportEmailTemplate";
import { DEMO_RUN } from "@/config";
import { getCsrfHeaders } from "@/lib/apiHeaders";
import type {
  WeeklyReport,
  EmailAttachment,
  EmailDeliveryStatus,
} from "@/types/WeeklyReport";
import { storage } from "./storage";

// Debug environment variables
console.log("GmailService - Environment variables:");
console.log("MODE:", import.meta.env.MODE);
console.log("DEV:", import.meta.env.DEV);
console.log("PROD:", import.meta.env.PROD);
// console.log('GOOGLE_CLIENT_ID:', import.meta.env.GOOGLE_CLIENT_ID);

// Function to safely encode unicode strings to base64
function unicodeSafeBase64Encode(str: string): string {
  // First encode the string as UTF-8
  const utf8Bytes = new TextEncoder().encode(str);

  // Convert the UTF-8 bytes to a binary string
  let binaryStr = "";
  utf8Bytes.forEach((byte) => {
    binaryStr += String.fromCharCode(byte);
  });

  // Use btoa on the binary string (which now only has bytes in the 0-255 range)
  const base64 = btoa(binaryStr);

  // Format as URL-safe base64
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

class GmailService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiryTime: number | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    // Gmail tokens are now stored securely on the backend only
    // No local storage of sensitive OAuth tokens
    console.log("GmailService - Initialized with backend-only token storage");
  }

  // Gmail tokens are now managed by the backend only
  // These methods are deprecated - tokens are not stored locally
  setAccessToken(token: string, expiresIn: number = 3600) {
    console.log("GmailService - Access token received, stored on backend only");
    this.accessToken = null; // Don't store locally
    this.tokenExpiryTime = null; // Don't track expiry locally
    // Tokens are managed by backend via /api/auth/gmail-token endpoint
  }

  setRefreshToken(token: string) {
    console.log(
      "GmailService - Refresh token received, stored on backend only",
    );
    this.refreshToken = null; // Don't store locally
    // Tokens are managed by backend
  }

  clearTokens() {
    console.log("GmailService - Clearing local token references");
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiryTime = null;
    // Backend tokens are cleared via logout endpoint
  }

  // Check if user has Gmail access (via backend)
  async hasAccessToken(): Promise<boolean> {
    try {
      const token = await this.getValidAccessToken();
      return !!token;
    } catch {
      return false;
    }
  }

  // Check if user has Gmail refresh token (via backend status)
  async hasRefreshToken(): Promise<boolean> {
    try {
      // This would need a backend endpoint to check refresh token status
      // For now, assume if we can get a valid access token, refresh token exists
      const token = await this.getValidAccessToken();
      return !!token;
    } catch {
      return false;
    }
  }

  // Token expiry is now managed by backend
  // Always assume token might be expired and fetch from backend
  isTokenExpired(): boolean {
    // Since tokens are managed by backend, we can't reliably check expiry locally
    // Always return true to force backend fetch
    return true;
  }

  // Request access using the provided token
  async requestAccess(
    token: string,
    refreshToken?: string,
    expiresIn?: number,
  ): Promise<void> {
    console.log("GmailService - Requesting Gmail access with token");
    this.setAccessToken(token, expiresIn);

    if (refreshToken) {
      this.setRefreshToken(refreshToken);
    }

    const isValid = await this.verifyAccessToken(token);
    if (!isValid) {
      throw new Error("Invalid Gmail access token");
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    // Use the backend endpoint which handles refresh automatically
    console.log("GmailService - Refreshing access token via backend...");

    try {
      const response = await fetch("/api/auth/gmail-token", {
        credentials: "include", // Send cookies for auth
        headers: getCsrfHeaders(),
      });

      console.log(
        "GmailService - Token refresh response status:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("GmailService - Token refresh failed:", errorData);
        return false;
      }

      const data = await response.json();

      if (data.access_token) {
        this.setAccessToken(data.access_token, data.expires_in || 3600);
        console.log("GmailService - Access token refreshed successfully");
        return true;
      }

      console.error("GmailService - Refresh response missing access_token");
      return false;
    } catch (error) {
      console.error("GmailService - Error refreshing token:", error);
      return false;
    }
  }

  // Proactively refresh token if needed (for session extension)
  async refreshTokenIfNeeded(): Promise<boolean> {
    console.log("GmailService - Checking if token refresh is needed");

    try {
      // Try to get a valid token - this will handle refresh automatically if expired
      const token = await this.getValidAccessToken();
      if (token) {
        console.log(
          "GmailService - Token is valid or was refreshed successfully",
        );
        return true;
      } else {
        console.log(
          "GmailService - No valid token available after refresh attempt",
        );
        return false;
      }
    } catch (error) {
      console.error(
        "GmailService - Error during proactive token refresh:",
        error,
      );
      return false;
    }
  }

  // Get a valid access token
  // Get valid access token from backend (no local storage)
  async getValidAccessToken(): Promise<string | null> {
    console.log("GmailService - Getting valid access token from backend");

    // If refresh is already in progress, wait for it
    if (this.refreshPromise) {
      console.log("GmailService - Refresh already in progress, waiting...");
      return this.refreshPromise;
    }

    // Always fetch from backend - no local token storage
    this.refreshPromise = this.fetchTokensFromBackend();
    console.log("GmailService - Fetching token from backend...");

    try {
      const token = await this.refreshPromise;
      if (token) {
        console.log("GmailService - Got valid token from backend");
        return token;
      } else {
        console.log("GmailService - No valid token available");
        return null;
      }
    } finally {
      // Clear the promise after completion
      this.refreshPromise = null;
      console.log("GmailService - Token fetch completed");
    }
  }

  // Backend handles all token management now
  private async fetchTokensFromBackend(): Promise<string | null> {
    try {
      console.log("GmailService - Fetching Gmail token from backend API...");
      const response = await fetch("/api/auth/gmail-token", {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest", // CSRF protection
        },
        credentials: "include", // Include httpOnly cookies
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log(
            "GmailService - No Gmail access, user needs to authenticate",
          );
          return null;
        }
        throw new Error(`Failed to fetch Gmail token: ${response.status}`);
      }

      const data = await response.json();
      const accessToken = data.access_token;

      if (accessToken) {
        console.log(
          "GmailService - Successfully received access token from backend",
        );
        return accessToken;
      } else {
        console.log("GmailService - No access token in response");
        return null;
      }
    } catch (error) {
      console.error("GmailService - Error fetching token from backend:", error);
      return null;
    }
  }

  // Use this method to test if the token is valid for Gmail API access
  async verifyAccessToken(accessToken?: string): Promise<boolean> {
    const token = accessToken || this.accessToken;
    if (!token) {
      console.warn("GmailService - No access token to verify");
      return false;
    }

    try {
      // Use tokeninfo endpoint - doesn't require additional scopes
      const tokenInfoResponse = await fetch(
        `https://oauth2.googleapis.com/tokeninfo?access_token=${token}`,
      );

      if (tokenInfoResponse.ok) {
        const data = await tokenInfoResponse.json();
        const hasGmailScope = data.scope?.includes("gmail.send");
        console.log(
          "GmailService - Token verified, has gmail.send:",
          hasGmailScope,
        );
        return hasGmailScope;
      }

      console.error(
        "GmailService - Token verification failed:",
        tokenInfoResponse.status,
      );
      return false;
    } catch (error) {
      console.error("GmailService - Error verifying token:", error);
      return false;
    }
  }

  async sendEmail(report: WeeklyReport): Promise<EmailDeliveryStatus> {
    // Generate unique ID for this send attempt to track duplicates
    const sendId = `send-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    try {
      console.log(`[${sendId}] GmailService - Starting email send process`);
      console.log(`[${sendId}] GmailService - Report details:`, {
        subject: report.subject,
        to: report.to,
        cc: report.cc,
        bcc: report.bcc,
        createdBy: report.createdBy,
        attachmentsCount: report.attachments?.length || 0,
        reportId: report.id,
      });

      // Get a valid access token (will refresh if needed)
      console.log(
        `[${sendId}] GmailService - Getting valid access token for email sending`,
      );
      const validToken = await this.getValidAccessToken();
      console.log(
        `[${sendId}] GmailService - Valid token available after refresh:`,
        !!validToken,
      );

      if (!validToken) {
        throw new Error(
          "No valid Gmail access token available. Please authenticate with Google first.",
        );
      }

      // Create email content using the WeeklyReportEmailTemplate
      const templateProps: WeeklyReportEmailTemplateProps = {
        name: report.createdBy || "User",
        dateRange: report.dateRange || report.date,
        last_week_tasks: report.last_week_tasks || [],
        Unplanned: report.Unplanned || [],
        blockers: report.blockers || [],
        next_week_tasks: report.next_week_tasks || [],
        further_beyond: report.further_beyond || [],
        notes: report.notes || "",
        greeting: report.greeting || "Hello",
        signature: report.signature || "",
      };

      const emailBody = WeeklyReportEmailTemplate(templateProps);

      // Construct recipients
      const to = Array.isArray(report.to)
        ? report.to.join(", ")
        : report.to || "";
      const cc = Array.isArray(report.cc)
        ? report.cc.join(", ")
        : report.cc || "";
      const bcc = Array.isArray(report.bcc)
        ? report.bcc.join(", ")
        : report.bcc || "";

      console.log(`[${sendId}] GmailService - Email recipients:`, {
        to,
        cc,
        bcc,
      });

      // Validate that at least one recipient exists
      if (!to.trim() && !cc.trim() && !bcc.trim()) {
        throw new Error(
          "Cannot send email: No recipients specified in To, CC, or BCC fields",
        );
      }

      // Build the email message (with or without attachments)
      let emailMessage: string;

      if (report.attachments && report.attachments.length > 0) {
        // Build MIME multipart message with attachments
        emailMessage = this.buildMimeMessageWithAttachments(
          to,
          cc,
          bcc,
          report.subject || "Weekly Report",
          emailBody,
          report.attachments,
        );
        console.log(
          `[${sendId}] GmailService - Built MIME message with`,
          report.attachments.length,
          "attachments",
        );
      } else {
        // Build simple HTML email without attachments
        const headers = [
          `Subject: ${report.subject || "Weekly Report"}`,
          "Content-Type: text/html; charset=utf-8",
          "MIME-Version: 1.0",
        ];

        // Only add recipient headers if they have actual recipients
        if (to.trim()) headers.push(`To: ${to}`);
        if (cc.trim()) headers.push(`Cc: ${cc}`);
        if (bcc.trim()) headers.push(`Bcc: ${bcc}`);

        emailMessage = [...headers, "", emailBody].join("\r\n");
      }

      const encodedEmail = unicodeSafeBase64Encode(emailMessage);
      console.log(
        `[${sendId}] GmailService - Email encoded, length:`,
        encodedEmail.length,
      );

      if (DEMO_RUN) {
        console.log(`[${sendId}] GmailService - Demo mode: Email prepared`);
        return {
          messageId: `demo-${Date.now()}`,
          threadId: `demo-thread-${Date.now()}`,
          sentAt: new Date().toISOString(),
          status: "sent",
        };
      }

      console.log(`[${sendId}] GmailService - Sending email via Gmail API NOW`);
      const response = await fetch(
        "https://gmail.googleapis.com/gmail/v1/users/me/messages/send",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${validToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            raw: encodedEmail,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `[${sendId}] GmailService - Gmail API error response:`,
          errorText,
        );
        return {
          sentAt: new Date().toISOString(),
          status: "failed",
          error: `Failed to send email: ${response.status} - ${errorText}`,
        };
      }

      const responseData = (await response.json()) as {
        id: string;
        threadId: string;
        labelIds?: string[];
      };
      console.log(
        `[${sendId}] GmailService - Email sent successfully! Message ID:`,
        responseData.id,
        "Thread ID:",
        responseData.threadId,
      );

      return {
        messageId: responseData.id,
        threadId: responseData.threadId,
        sentAt: new Date().toISOString(),
        status: "sent",
        labels: responseData.labelIds,
        sendId: sendId, // Include sendId for tracking
      } as EmailDeliveryStatus & { sendId: string };
    } catch (error) {
      console.error(`[${sendId}] GmailService - Error sending email:`, error);
      return {
        sentAt: new Date().toISOString(),
        status: "failed",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Build a MIME multipart message with attachments
   */
  private buildMimeMessageWithAttachments(
    to: string,
    cc: string,
    bcc: string,
    subject: string,
    htmlBody: string,
    attachments: EmailAttachment[],
  ): string {
    const boundary = `----=_Part_${Date.now()}_${Math.random().toString(36).substring(2)}`;

    // Build headers
    const headers: string[] = [
      `Subject: ${subject}`,
      "MIME-Version: 1.0",
      `Content-Type: multipart/mixed; boundary="${boundary}"`,
    ];

    // Only add recipient headers if they have actual recipients
    if (to.trim()) headers.push(`To: ${to}`);
    if (cc.trim()) headers.push(`Cc: ${cc}`);
    if (bcc.trim()) headers.push(`Bcc: ${bcc}`);

    // Start building the message
    const parts: string[] = [
      headers.join("\r\n"),
      "", // Empty line after headers
      `--${boundary}`,
      "Content-Type: text/html; charset=utf-8",
      "Content-Transfer-Encoding: 7bit",
      "",
      htmlBody,
    ];

    // Add each attachment
    for (const attachment of attachments) {
      parts.push(
        `--${boundary}`,
        `Content-Type: ${attachment.mimeType}; name="${attachment.filename}"`,
        "Content-Transfer-Encoding: base64",
        `Content-Disposition: attachment; filename="${attachment.filename}"`,
        "",
        // Split base64 content into 76-character lines (RFC 2045)
        attachment.content.match(/.{1,76}/g)?.join("\r\n") ||
          attachment.content,
      );
    }

    // Close the MIME message
    parts.push(`--${boundary}--`);

    return parts.join("\r\n");
  }

  /**
   * Get email delivery status by message ID
   */
  async getDeliveryStatus(
    messageId: string,
  ): Promise<EmailDeliveryStatus | null> {
    const validToken = await this.getValidAccessToken();
    if (!validToken) {
      return null;
    }

    try {
      const response = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}?format=metadata`,
        {
          headers: {
            Authorization: `Bearer ${validToken}`,
          },
        },
      );

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as {
        id: string;
        threadId: string;
        labelIds?: string[];
        internalDate: string;
      };

      return {
        messageId: data.id,
        threadId: data.threadId,
        sentAt: new Date(parseInt(data.internalDate)).toISOString(),
        status: "sent",
        labels: data.labelIds,
      };
    } catch (error) {
      console.error("GmailService - Error getting delivery status:", error);
      return null;
    }
  }

  /**
   * Check for duplicate emails sent to a specific recipient within a time window
   * Useful for debugging double-send issues
   */
  async checkForDuplicateSends(
    subject: string,
    recipientEmail: string,
    timeWindowMinutes: number = 5,
  ): Promise<{
    count: number;
    messages: Array<{ id: string; threadId: string; sentAt: string }>;
  }> {
    const validToken = await this.getValidAccessToken();
    if (!validToken) {
      console.warn(
        "GmailService - Cannot check for duplicates: No valid token",
      );
      return { count: 0, messages: [] };
    }

    try {
      const startTime = Date.now() - timeWindowMinutes * 60 * 1000;
      const query = `subject:"${subject}" to:${recipientEmail} OR bcc:${recipientEmail} OR cc:${recipientEmail} after:${Math.floor(startTime / 1000)}`;

      console.log("GmailService - Checking for duplicates with query:", query);

      const response = await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${validToken}`,
          },
        },
      );

      if (!response.ok) {
        console.error("GmailService - Error querying Gmail:", response.status);
        return { count: 0, messages: [] };
      }

      const data = (await response.json()) as {
        messages?: Array<{ id: string; threadId: string }>;
        resultSizeEstimate?: number;
      };

      const messages = data.messages || [];
      const messageDetails = await Promise.all(
        messages.map(async (msg) => {
          try {
            const msgResponse = await fetch(
              `https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}?format=metadata&metadataHeaders=Date`,
              {
                headers: {
                  Authorization: `Bearer ${validToken}`,
                },
              },
            );
            if (msgResponse.ok) {
              const msgData = (await msgResponse.json()) as {
                internalDate: string;
              };
              return {
                id: msg.id,
                threadId: msg.threadId,
                sentAt: new Date(parseInt(msgData.internalDate)).toISOString(),
              };
            }
          } catch (e) {
            console.error("GmailService - Error fetching message details:", e);
          }
          return {
            id: msg.id,
            threadId: msg.threadId,
            sentAt: "unknown",
          };
        }),
      );

      console.log(
        `GmailService - Found ${messageDetails.length} messages matching criteria`,
      );
      return {
        count: messageDetails.length,
        messages: messageDetails,
      };
    } catch (error) {
      console.error("GmailService - Error checking for duplicates:", error);
      return { count: 0, messages: [] };
    }
  }
}

export const gmailService = new GmailService();
